import { getRouteApi } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTitle } from "react-use";

import { defaultExploreFilterState } from "@blis/insights/explore/explore-filter-state";
import {
  DEFAULT_EXPLORE_ENTITY,
  defaultExploreUrlState,
} from "@blis/insights/explore/explore-url-state";
import { type RouterOutputs, trpc } from "@blis/trpc/react";

import { SubHeader } from "@/components/SubHeader";
import { Breadcrumb, Breadcrumbs } from "@/components/ui/breadcrumbs";
import { Label } from "@/components/ui/field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Text } from "@/components/ui/text";
import { useExploreUrlState } from "../explore/useExploreUrlState";
import { ExploreEntityGrid } from "./ExploreEntityGrid";

const routeApi = getRouteApi("/_auth/_app/insights/explore");

export function ExplorePage() {
  useTitle("Explore");
  const [loadError, setLoadError] = useState<string | null>(null);
  const search = routeApi.useSearch();
  const { data: entities } = trpc.explore.entities.useQuery();
  const entityNames = useMemo(
    () => entities?.map((entry) => entry.name) ?? [],
    [entities],
  );

  const entity = useMemo(() => {
    const requested =
      search.entity && entityNames.includes(search.entity)
        ? search.entity
        : DEFAULT_EXPLORE_ENTITY;
    return entities?.find((entry) => entry.name === requested) ?? entities?.[0];
  }, [entities, entityNames, search.entity]);

  const entityMeta = useMemo(
    () => (entity ? buildEntityUrlMeta(entity) : null),
    [entity],
  );

  const { urlState, defaults, patchUrlState, setUrlState } = useExploreUrlState(
    entityMeta,
    entityNames,
  );

  const modeLabel = urlState.view === "summary" ? "Summary" : "Rows";

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <SubHeader>
        <SubHeader.Left className="gap-3">
          <Breadcrumbs>
            <Breadcrumb href={{ to: "/insights" }}>Insights</Breadcrumb>
            <Breadcrumb>Explore</Breadcrumb>
          </Breadcrumbs>
          <span
            className={
              urlState.view === "summary"
                ? "rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                : "rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
            }
          >
            {modeLabel}
          </span>
          <Text size="2" muted className="hidden min-w-0 truncate xl:block">
            {urlState.view === "summary"
              ? "Grouped chart and table from the current filters."
              : "Raw rows with server-side paging."}
          </Text>
        </SubHeader.Left>
        <SubHeader.Right>
          <div className="flex items-center gap-2">
            <Label
              htmlFor="explore-entity"
              className="text-sm text-muted-foreground"
            >
              Dataset
            </Label>
            <Select
              id="explore-entity"
              aria-label="Dataset"
              selectedKey={urlState.entity}
              onSelectionChange={(key) => {
                if (key == null || String(key) === urlState.entity) {
                  return;
                }
                const nextEntity = String(key);
                const nextEntityRecord = entities?.find(
                  (entry) => entry.name === nextEntity,
                );
                const nextMeta = nextEntityRecord
                  ? buildEntityUrlMeta(nextEntityRecord)
                  : null;
                const nextDefaults = nextMeta
                  ? defaultExploreUrlState(
                      nextMeta.groupableFields,
                      nextMeta.bucketableFields,
                    )
                  : defaultExploreUrlState();
                setUrlState({
                  ...nextDefaults,
                  entity: nextEntity,
                  view: "rows",
                  filterState: defaultExploreFilterState(),
                });
                setLoadError(null);
              }}
              className="w-44"
            >
              <SelectTrigger />
              <SelectList items={entities ?? []}>
                {(item) => (
                  <SelectItem id={item.name} textValue={item.label}>
                    {item.label}
                  </SelectItem>
                )}
              </SelectList>
            </Select>
          </div>
        </SubHeader.Right>
      </SubHeader>

      {loadError ? (
        <div className="shrink-0 border-b border-destructive/40 bg-destructive/5 px-4 py-2 text-sm text-destructive">
          {loadError}
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col">
        {entity ? (
          <ExploreEntityGrid
            key={entity.name}
            entity={entity}
            urlState={urlState}
            onUrlStateChange={patchUrlState}
            onLoadError={setLoadError}
          />
        ) : null}
      </div>
    </div>
  );
}

function buildEntityUrlMeta(
  entity: RouterOutputs["explore"]["entities"][number],
) {
  const groupableFields = entity.columns
    .filter((column) => column.groupable && !column.phi)
    .map((column) => column.field);
  const bucketableFields = new Set(
    entity.columns
      .filter((column) => column.bucketable)
      .map((column) => column.field),
  );
  const measureIds = new Set<string>(["count:*"]);
  for (const column of entity.columns) {
    if (!column.aggregatable || !column.aggFuncs) {
      continue;
    }
    for (const func of column.aggFuncs) {
      measureIds.add(`${func}:${column.field}`);
    }
  }

  return {
    name: entity.name,
    groupableFields,
    bucketableFields,
    measureIds,
    chartIds: new Set(["auto", "bar", "line", "donut"]),
  };
}
